/**
 * SetupIntent + Payment Element controller for $0-total bookings whose
 * channel manager still requires a card on file.
 *
 * Lifecycle:
 *   1. connect()  fetches a SetupIntent client_secret from the backend
 *                 (GET /api/payments/create-setup-intent/?order_id=...)
 *                 and mounts a Payment Element in setup mode.
 *   2. submit     stripe.confirmSetup(redirect: 'if_required'). On success
 *                 the SetupIntent comes back with payment_method=pm_xxx.
 *   3. We stuff   pm_xxx into the form's hidden `stripe_payment_method_id`
 *                 input and submit the form for real, letting the server
 *                 redirect to the free-processing page.
 *
 * 3DS / off-session redirects: SetupIntents for $0 verification rarely
 * trigger 3DS, but if they do Stripe will redirect to confirmParams.return_url.
 * For now we use the current URL as return_url and rely on `redirect:
 * 'if_required'` so the typical flow stays inline. If you start seeing
 * 3DS-required cards, add a query-param-based recovery path here.
 */

import { Controller } from '@hotwired/stimulus';

interface StripeInstance {
  elements: (options: { appearance: { theme: string }; clientSecret: string }) => StripeElements;
  confirmSetup: (options: {
    elements: StripeElements;
    confirmParams: { return_url: string };
    redirect?: 'always' | 'if_required';
  }) => Promise<{
    error?: StripeError;
    setupIntent?: { id: string; payment_method: string; status: string };
  }>;
}

interface StripeElements {
  create: (type: string, options: { layout: string }) => StripeElement;
  submit: () => Promise<{ error?: StripeError }>;
}

interface StripeElement {
  mount: (selector: string | HTMLElement) => void;
}

interface StripeError {
  type: string;
  message: string;
}

declare const Stripe: (publishableKey: string) => StripeInstance;

export default class extends Controller<HTMLElement> {
  static targets = [
    'form',
    'submit',
    'spinner',
    'buttonText',
    'message',
    'paymentElement',
    'paymentMethodInput',
  ];

  static values = {
    publishableKey: String,
    setupIntentUrl: String,
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly spinnerTarget: HTMLElement;
  declare readonly buttonTextTarget: HTMLElement;
  declare readonly messageTarget: HTMLElement;
  declare readonly paymentElementTarget: HTMLElement;
  declare readonly paymentMethodInputTarget: HTMLInputElement;

  declare readonly hasSpinnerTarget: boolean;
  declare readonly hasButtonTextTarget: boolean;
  declare readonly hasMessageTarget: boolean;

  declare readonly publishableKeyValue: string;
  declare readonly setupIntentUrlValue: string;

  private stripe!: StripeInstance;
  private elements!: StripeElements;
  private confirmedPaymentMethodId: string | null = null;

  async connect(): Promise<void> {
    if (this.element.dataset.setupIntentInitialized === 'true') {
      return;
    }
    this.element.dataset.setupIntentInitialized = 'true';

    this.stripe = Stripe(this.publishableKeyValue);
    await this.loadAndMount();
  }

  private async loadAndMount(): Promise<void> {
    try {
      const response = await fetch(this.setupIntentUrlValue, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new Error(`SetupIntent fetch failed: ${response.status}`);
      }
      const { clientSecret } = (await response.json()) as { clientSecret: string };

      this.elements = this.stripe.elements({
        appearance: { theme: 'stripe' },
        clientSecret,
      });
      const paymentElement = this.elements.create('payment', { layout: 'accordion' });
      paymentElement.mount(this.paymentElementTarget);
      this.paymentElementTarget.querySelector('p')?.remove();
    } catch (error) {
      this.showMessage('Could not initialize the card form. Please refresh and try again.');
      // eslint-disable-next-line no-console
      console.error('SetupIntent init error:', error);
    }
  }

  async handleSubmit(event: Event): Promise<void> {
    // First submission: run Stripe confirmSetup, capture pm_id, then re-submit
    // the form (now bearing the hidden input) to the server.
    if (this.confirmedPaymentMethodId) {
      // Second pass — let the form post normally to the server.
      return;
    }
    event.preventDefault();
    this.setLoading(true);
    this.clearMessage();

    const { error, setupIntent } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      this.showMessage(error.message || 'Could not save card. Please try again.');
      this.setLoading(false);
      return;
    }

    if (!setupIntent || !setupIntent.payment_method) {
      this.showMessage('Card saved, but no payment method id returned. Please retry.');
      this.setLoading(false);
      return;
    }

    if (setupIntent.status !== 'succeeded') {
      this.showMessage(`Card setup not yet complete (status: ${setupIntent.status}).`);
      this.setLoading(false);
      return;
    }

    this.confirmedPaymentMethodId = setupIntent.payment_method;
    this.paymentMethodInputTarget.value = setupIntent.payment_method;
    // Submit again — the early-return guard above lets it through this time.
    this.formTarget.submit();
  }

  private showMessage(text: string): void {
    if (!this.hasMessageTarget) return;
    this.messageTarget.textContent = text;
    this.messageTarget.classList.remove('hidden');
  }

  private clearMessage(): void {
    if (!this.hasMessageTarget) return;
    this.messageTarget.textContent = '';
    this.messageTarget.classList.add('hidden');
  }

  private setLoading(isLoading: boolean): void {
    this.submitTarget.disabled = isLoading;
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle('hidden', !isLoading);
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.classList.toggle('hidden', isLoading);
    }
  }
}
